import type { TransactionType } from '../types/wallet';

const TRANSACTION_TYPE_LABELS: Record<TransactionType, string> = {
  PaymentGatewayCharge: 'شحن عبر بوابة الدفع',
  WalletCodeCharge: 'شحن بكود المحفظة',
  CoinsUsage: 'شراء من المحفظة',
  DirectCoursePurchase: 'شراء كورس',
};

export function getTransactionTypeLabel(type: string): string {
  return TRANSACTION_TYPE_LABELS[type as TransactionType] ?? type;
}

export function formatWalletDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatCoins(amount: number): string {
  return Math.abs(amount).toLocaleString('ar-EG');
}

export function formatCoinsDelta(amount: number): string {
  const sign = amount > 0 ? '+' : amount < 0 ? '−' : '';
  return `${sign}${formatCoins(amount)}`;
}

/** Localize English backend transaction descriptions to Egyptian Arabic. */
export function formatTransactionDescription(
  description: string | undefined,
  type?: string
): string {
  if (!description?.trim()) {
    return type ? getTransactionTypeLabel(type) : '';
  }

  const d = description.trim();

  const purchasedCourse = /^Purchased course:\s*(.+)$/i.exec(d);
  if (purchasedCourse) {
    return `شراء كورس: ${purchasedCourse[1].trim()}`;
  }

  const purchasedUnit = /^Purchased unit:\s*(.+)$/i.exec(d);
  if (purchasedUnit) {
    return `شراء حصة: ${purchasedUnit[1].trim()}`;
  }

  const walletRecharge = /^Recharged\s+(\d+)\s+coins?\s+using\s+wallet\s+code:\s*(\S+)$/i.exec(d);
  if (walletRecharge) {
    const coins = Number(walletRecharge[1]);
    return `شحن ${formatCoins(coins)} جنيه بكود المحفظة: ${walletRecharge[2]}`;
  }

  const gatewayCourse = /^Purchased course via payment gateway \(Amount:\s*([\d.]+)\s*EGP\)$/i.exec(d);
  if (gatewayCourse) {
    return `شراء كورس عبر بوابة الدفع (المبلغ: ${formatCoins(Number(gatewayCourse[1]))} جنيه)`;
  }

  const gatewayUnit = /^Purchased unit via payment gateway \(Amount:\s*([\d.]+)\s*EGP\)$/i.exec(d);
  if (gatewayUnit) {
    return `شراء حصة عبر بوابة الدفع (المبلغ: ${formatCoins(Number(gatewayUnit[1]))} جنيه)`;
  }

  const gatewayCharge =
    /^Charged\s+(\d+)\s+coins?\s+via\s+payment\s+gateway\s+\(Amount:\s*([\d.]+)\s*EGP\)(?:\s*-\s*completed by admin)?$/i.exec(
      d
    );
  if (gatewayCharge) {
    const coins = Number(gatewayCharge[1]);
    const amount = formatCoins(Number(gatewayCharge[2]));
    const base = `شحن ${formatCoins(coins)} جنيه عبر بوابة الدفع (المبلغ: ${amount} جنيه)`;
    return /completed by admin/i.test(d) ? `${base} — تم بواسطة المشرف` : base;
  }

  const walletCharge = /^Wallet Charge\s+(\d+)\s+Coins?$/i.exec(d);
  if (walletCharge) {
    return `شحن ${formatCoins(Number(walletCharge[1]))} جنيه للمحفظة`;
  }

  const paymentOrder = /^Payment for Order\s+(.+)$/i.exec(d);
  if (paymentOrder) {
    return `دفع للطلب ${paymentOrder[1].trim()}`;
  }

  const adminGrant = /^Admin granted\s+(\d+)\s+coins?\.\s*(.*)$/i.exec(d);
  if (adminGrant) {
    const reason = adminGrant[2].trim();
    const base = `منح إداري: ${formatCoins(Number(adminGrant[1]))} جنيه`;
    return reason ? `${base}. ${reason}` : base;
  }

  const adminCourse = /^Admin enrolled student in course:\s*(.+?)\.\s*(.*)$/i.exec(d);
  if (adminCourse) {
    const reason = adminCourse[2].trim();
    const base = `تسجيل إداري في كورس: ${adminCourse[1].trim()}`;
    return reason ? `${base}. ${reason}` : base;
  }

  const adminUnit = /^Admin enrolled student in unit:\s*(.+?)\.\s*(.*)$/i.exec(d);
  if (adminUnit) {
    const reason = adminUnit[2].trim();
    const base = `تسجيل إداري في حصة: ${adminUnit[1].trim()}`;
    return reason ? `${base}. ${reason}` : base;
  }

  return d;
}

export const WALLET_CODE_PATTERN = /^\d{7}$/;

export function isValidWalletCode(code: string): boolean {
  return WALLET_CODE_PATTERN.test(code.trim());
}
